import React, { useState } from "react";
import axios from "axios";
import { Loader2, MailCheck } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Shared "Forgot password?" link + inline request form for all portal logins.
// The backend always returns success (never reveals whether an account exists).
export const ForgotPassword = ({ testidPrefix, defaultEmail = "" }) => {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState(defaultEmail);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) { setError("Enter your email address."); return; }
    setError("");
    setBusy(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: email.trim() });
      setDone(true);
    } catch {
      setError("Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <button
        type="button"
        data-testid={`${testidPrefix}-forgot-link`}
        onClick={() => setOpen(true)}
        className="text-sm font-semibold text-[#54BCE6] hover:text-[#133955] transition-colors"
      >
        Forgot password?
      </button>
    );
  }

  if (done) {
    return (
      <div data-testid={`${testidPrefix}-forgot-sent`} className="rounded-xl bg-[#EAF7FC] border border-[#54BCE6]/30 p-4 flex items-start gap-3">
        <MailCheck size={18} className="text-[#133955] shrink-0 mt-0.5" />
        <p className="text-sm text-[#173042]/80">
          If an account exists for <strong className="text-[#133955]">{email}</strong>, a password reset link is on its way. It works once and expires in 1 hour.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} data-testid={`${testidPrefix}-forgot-form`} className="rounded-xl bg-[#EAF7FC] p-4 space-y-3">
      <p className="text-xs font-bold uppercase tracking-wide text-[#133955]">Reset your password</p>
      <input
        data-testid={`${testidPrefix}-forgot-email`}
        type="email"
        className="hpc-input bg-white"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="you@company.com"
      />
      {error && <p data-testid={`${testidPrefix}-forgot-error`} className="text-sm text-red-600">{error}</p>}
      <div className="flex items-center gap-3">
        <button
          type="submit"
          data-testid={`${testidPrefix}-forgot-submit`}
          disabled={busy}
          className="inline-flex items-center gap-2 rounded-full bg-[#133955] text-white px-5 py-2.5 text-sm font-bold hover:bg-[#54BCE6] hover:text-[#133955] transition-colors disabled:opacity-60"
        >
          {busy && <Loader2 size={14} className="animate-spin" />}
          Email Reset Link
        </button>
        <button
          type="button"
          data-testid={`${testidPrefix}-forgot-cancel`}
          onClick={() => { setOpen(false); setError(""); }}
          className="text-sm font-semibold text-[#173042]/60 hover:text-[#9b1c1c]"
        >
          Cancel
        </button>
      </div>
    </form>
  );
};
